import { action, computed, makeObservable, observable } from 'mobx';

import Auth from './AuthManager';

import { NOTIFICATION_ENTITY_TYPE, NOTIFICATION_NAV_MAX_COUNT, NOTIFICATION_NAV_TYPE } from '../constants';

export const NOTIFICATION_ENDPOINTS = {
  CREATE_ASSESSMENT_SHARE_NOTIFICATION: '/api/createShareAssessmentNotification',
  DELETE_NOTIFICATION: '/api/deleteNotification',
  FETCH_MANUAL_NOTIFICATIONS: '/api/viewSatelliteMessagesByUser',
  FETCH_SYSTEM_NOTIFICATION: '/api/viewNotification',
  FETCH_SYSTEM_NOTIFICATIONS: '/api/viewNotifications'
};

export class NotificationManager {
  constructor() {
    makeObservable(this);
  }

  @observable activeNotificationType = NOTIFICATION_NAV_TYPE.MANUAL;

  // message notifications (manually sent from a publisher user)
  @observable loadedManualNotifications = false;
  @observable hasMoreManualNotifications = false;
  @observable manualNotificationCount = 0;
  @observable manualNotificationMessages = [];

  // system notifications (auto generated - e.g. after requesting a gradebook/report csv export)
  @observable loadedSystemNotifications = false;
  @observable hasMoreSystemNotifications = false;
  @observable systemNotificationCount = 0;
  @observable systemNotificationMessages = [];

  @observable systemNotification = '';

  @action clearAll = () => {
    this.activeNotificationType = NOTIFICATION_NAV_TYPE.MANUAL;
    // clear user notifications
    this.loadedManualNotifications = false;
    this.hasMoreManualNotifications = false;
    this.manualNotificationCount = 0;
    this.manualNotificationMessages = [];
    // clear system notifications
    this.loadedSystemNotifications = false;
    this.hasMoreSystemNotifications = false;
    this.systemNotificationCount = 0;
    this.systemNotificationMessages = [];
  }

  @action setActiveNotificationType = (notificationsType) => {
    this.activeNotificationType = notificationsType;
  }

  @action setSystemNotification = (obj) => {
    this.systemNotification = obj;
  }

  /**
   * Fetch **User Notifications** - i.e. messages that were manually created by a publisher user and addressed to the current user
   */
  @action fetchManualNotifications = async ({
    page = 0,
    pageSize = this.MANUAL_NOTIFICATIONS_PAGE_SIZE,
    sortDirection = 'desc',
    sortField = 'startDate',
    userId
  } = {}) => {
    try {
      this.setLoadedManualNotifications(false);

      const { FETCH_MANUAL_NOTIFICATIONS } = NOTIFICATION_ENDPOINTS;
      let apiUrl = `${Auth.ecms}${FETCH_MANUAL_NOTIFICATIONS}?userId=${userId}`;
      apiUrl += `&pageSize=${pageSize}`;
      apiUrl += `&skip=${page * pageSize}`;
      apiUrl += `&sort[0][field]=${sortField}`;
      apiUrl += `&sort[0][dir]=${sortDirection}`;

      const response = await Auth.fetch(apiUrl, {
        method: 'GET'
      });

      if (response.status === 'SUCCESS') {
        const pageTotal = typeof +response.pageTotal === 'number' ? +response.pageTotal : 0;

        const notifications = page ? (
          [...this.manualNotificationMessages, ...response.data]
        ) : [...response.data];

        this.setManualNotificationCount(pageTotal);
        this.setManualNotificationMessages(notifications);

        if (notifications?.length < pageTotal) {
          this.setLoadedManualNotifications(true);
          this.setHasMoreManualNotifications(true);
        } else {
          this.setLoadedManualNotifications(true);
          this.setHasMoreManualNotifications(false);
        }
      } else {
        this.setLoadedManualNotifications(true);
        this.setHasMoreManualNotifications(false);
      }
      return null;
    } catch (error) {
      console.error(error);
      this.setLoadedManualNotifications(true);
      this.setHasMoreManualNotifications(false);
    }
  }

  @action setLoadedManualNotifications = (loaded) => {
    this.loadedManualNotifications = loaded;
  }

  @action setHasMoreManualNotifications = (hasMore) => {
    this.hasMoreManualNotifications = hasMore;
  }

  @action setManualNotificationCount = (count) => {
    this.manualNotificationCount = count;
  }

  @action setManualNotificationMessages = (messages) => {
    this.manualNotificationMessages = messages;
  }

  /**
   * Fetch **System Notifications** - i.e. auto-generated messages created after the user performs a certain action
   * (e.g. requesting a gradebook/report export)
   */
  @action fetchSystemNotifications = async ({
    page = 0,
    pageSize = this.SYSTEM_NOTIFICATIONS_PAGE_SIZE,
    searchTerms = [], // we want all notifications now
    searchTypes = [], // we want all notifications now
    sortDirection = 'desc',
    sortField = 'creationDate'
    // userId
  } = {}) => {
    try {
      this.setLoadedSystemNotifications(false);

      const { FETCH_SYSTEM_NOTIFICATIONS } = NOTIFICATION_ENDPOINTS;
      let apiUrl = `${Auth.ecms}${FETCH_SYSTEM_NOTIFICATIONS}`/* ?userId=${userId}` */;
      apiUrl += `?searchTypes=${searchTypes.toString()}`;
      apiUrl += `&searchTerms=${searchTerms.toString()}`;
      apiUrl += `&pageSize=${pageSize}`;
      apiUrl += `&skip=${page * pageSize}`;
      apiUrl += `&sort[0][field]=${sortField}`;
      apiUrl += `&sort[0][dir]=${sortDirection}`;

      const response = await Auth.fetch(apiUrl, {
        method: 'GET'
      });

      if (response.status === 'SUCCESS') {
        const pageTotal = typeof +response.pageTotal === 'number' ? +response.pageTotal : 0;

        const notifications = page ? (
          [...this.systemNotificationMessages, ...response.notifications]
        ) : [...response.notifications];

        this.setSystemNotificationCount(pageTotal);
        this.setSystemNotificationMessages(notifications);

        if (notifications?.length < pageTotal) {
          this.setLoadedSystemNotifications(true);
          this.setHasMoreSystemNotifications(true);
        } else {
          this.setLoadedSystemNotifications(true);
          this.setHasMoreSystemNotifications(false);
        }
      } else {
        this.setLoadedSystemNotifications(true);
        this.setHasMoreManualNotifications(false);
      }
      return null;
    } catch (error) {
      console.error(error);
      this.setLoadedSystemNotifications(true);
      this.setHasMoreSystemNotifications(false);
    }
  }

  @action fetchSystemNotification = async (id) => {
    try {
      const { FETCH_SYSTEM_NOTIFICATION } = NOTIFICATION_ENDPOINTS;
      let apiUrl = `${Auth.ecms}${FETCH_SYSTEM_NOTIFICATION}`;
      apiUrl += `?id=${id}`;

      const response = await Auth.fetch(apiUrl, {
        method: 'GET'
      });
      if (response.status === 'SUCCESS') {
        this.setSystemNotification(response.data[0]);
      } else {
        console.error('Failed to retrieve notification');
      }
      return null;
    } catch (error) {
      console.error(error);
    }
  }

  @action deleteNotification = async (notificationId) => {
    try {
      if (!notificationId || typeof notificationId !== 'string') {
        return;
      }
      const { DELETE_NOTIFICATION } = NOTIFICATION_ENDPOINTS;
      const apiUrl = `${Auth.ecms}${DELETE_NOTIFICATION}`;

      await Auth.fetch(apiUrl, {
        body: {
          id: notificationId
        },
        method: 'POST'
      });
    } catch (error) {
      console.error(error);
    }
  };

  @action addShareNotification = async (
    assessmentName,
    allowEdit,
    contentItemId,
    contentItemEntityId,
    contentItemEntityTypeId,
    message,
    selectedUsers) => {
    try {
      if (!contentItemEntityId || typeof contentItemEntityId !== 'string') {
        return;
      }
      const { CREATE_ASSESSMENT_SHARE_NOTIFICATION } = NOTIFICATION_ENDPOINTS;
      const apiUrl = `${Auth.ecms}${CREATE_ASSESSMENT_SHARE_NOTIFICATION}`;
      const data = await Auth.fetch(apiUrl, {
        body: {
          assessmentName,
          allowEdit,
          contentItemId,
          contentItemEntityId,
          contentItemEntityTypeId,
          message,
          userIds: selectedUsers.join(',')
        },
        method: 'POST'
      });

      if (data && data.status === 'SUCCESS') {
        return true;
      }
      return false;
    } catch (error) {
      console.error(error);
    }
  };

  @action setLoadedSystemNotifications = (loaded) => {
    this.loadedSystemNotifications = loaded;
  }

  @action setHasMoreSystemNotifications = (hasMore) => {
    this.hasMoreSystemNotifications = hasMore;
  }

  @action setSystemNotificationCount = (count) => {
    this.systemNotificationCount = count;
  }

  @action setSystemNotificationMessages = (messages) => {
    this.systemNotificationMessages = messages;
  }

  @computed get loaded() {
    return this.loadedManualNotifications && this.loadedSystemNotifications;
  }

  @computed get topNavNotificationCountLabel() {
    const count = this.totalNotificationCount;
    if (count > NOTIFICATION_NAV_MAX_COUNT) {
      return `${NOTIFICATION_NAV_MAX_COUNT}+`;
    } else {
      return `${count}`;
    }
  }

  @computed get totalNotificationCount() {
    const total = (this.manualNotificationCount || 0) + (this.systemNotificationCount || 0);
    return total;
  }

  @computed get MANUAL_NOTIFICATIONS_PAGE_SIZE() {
    return 25;
  }

  @computed get SYSTEM_NOTIFICATIONS_PAGE_SIZE() {
    return 25;
  }

  getSystemNotificationMessage = (key) => {
    if (key && this.systemNotificationMessages.has(key)) {
      return this.systemNotificationMessages.get(key);
    }
    return null;
  }
}

export default new NotificationManager();
